import { Pencil, Undo2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { frameLabel } from '@/lib/frame-label';
import type { CorrectionReason } from '@/lib/api/types';
import { CORRECTION_REASONS, STAGE_LABEL, editableValue, humanise, type AttributeView } from './review-model';

interface AttributeRowProps {
  attribute: AttributeView;
  assetKeys: string[];
  /** The draft value, when the row is being edited or filled. */
  draft: string | undefined;
  reason: CorrectionReason | undefined;
  disabled: boolean;
  onDraft: (value: string | undefined) => void;
  onReason: (reason: CorrectionReason | undefined) => void;
}

/**
 * One attribute of the item standard. A value the extraction read shows
 * with its frame label and lineage badge, and turns into an input on
 * "Edit". An empty attribute is an input from the start, so the operator
 * fills it where it sits. Either way the reason chips below are optional:
 * a correction without a reason is still a correction.
 */
export function AttributeRow({ attribute, assetKeys, draft, reason, disabled, onDraft, onReason }: AttributeRowProps) {
  const { name, field } = attribute;
  const label = humanise(name);
  const frame = field ? frameLabel(field, assetKeys) : null;
  const editing = draft !== undefined;
  const alwaysInput = !field;
  const inputId = `attribute-${name}`;
  const changed = editing && (field ? draft !== editableValue(field) : draft.trim() !== '');

  return (
    <li
      className={cn(
        'flex flex-col gap-2 rounded-card border p-3 text-sm',
        attribute.missingRequired ? 'border-warning' : 'border-border',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <label
          htmlFor={editing || alwaysInput ? inputId : undefined}
          className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1 text-muted">
          <span className="text-surface-foreground">{label}</span>
          {attribute.required && <span className="text-xs">Required</span>}
          {frame && <span className="text-xs">{frame}</span>}
        </label>
        <div className="flex shrink-0 flex-wrap justify-end gap-1">
          {attribute.missingRequired && <Badge tone="warning">Required, empty</Badge>}
          {!attribute.missingRequired && alwaysInput && <Badge>Empty</Badge>}
          {field && (
            <Badge tone={field.verified ? 'success' : attribute.lowConfidence ? 'warning' : 'neutral'}>
              {STAGE_LABEL[field.stage]}
              {field.confidence !== undefined && !field.verified && ` ${Math.round(field.confidence * 100)}%`}
            </Badge>
          )}
        </div>
      </div>

      {editing || alwaysInput ? (
        <div className="flex items-center gap-2">
          <Input
            id={inputId}
            value={draft ?? ''}
            disabled={disabled}
            placeholder={alwaysInput ? `Add ${label.toLowerCase()}` : undefined}
            onChange={(event) => onDraft(event.target.value)}
          />
          {field && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Undo the change to ${label}`}
              disabled={disabled}
              onClick={() => {
                onDraft(undefined);
                onReason(undefined);
              }}
            >
              <Undo2 aria-hidden="true" />
            </Button>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <span className="min-w-0 break-words">
            {editableValue(field)}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Edit ${label}`}
            disabled={disabled}
            onClick={() => onDraft(editableValue(field))}
          >
            <Pencil aria-hidden="true" />
          </Button>
        </div>
      )}

      {field?.machineValue && (
        <p className="text-xs text-muted">
          The machine read &ldquo;{String(field.machineValue.value ?? 'nothing')}&rdquo; (
          {STAGE_LABEL[field.machineValue.stage]}).
        </p>
      )}

      {changed && (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1 text-xs text-muted">Why did it change? Optional.</legend>
          <div className="flex flex-wrap gap-1.5">
            {CORRECTION_REASONS.map((option) => {
              const selected = reason === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected}
                  disabled={disabled}
                  onClick={() => onReason(selected ? undefined : option.value)}
                  className={cn(
                    'min-h-11 rounded-control border px-3 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                    selected ? 'border-accent bg-accent/20 text-surface-foreground' : 'border-border text-muted',
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}
    </li>
  );
}
